import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Container, Grid, Stack, Typography, Alert, CircularProgress } from '@material-ui/core';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Page from '../components/Page';
import Breadcrumb from '../components/common/Breadcrumb';
import MonthPicker, {
  formatMonthParam,
  initialMonthFromUrl
} from '../components/billing/MonthPicker';
import DeploymentsKpiRow from '../components/billing/DeploymentsKpiRow';
import DeploymentsPeakUsageAndTableCard from '../components/billing/DeploymentsPeakUsageAndTableCard';
import useVrackApi from '../hooks/useVrackApi';
import { setOpenSnackbar } from '../store/slices/snackbarSlice';
import {
  computePeakConcurrentByDay,
  toBaseDeploymentRow,
  wasActiveOnDay,
  DEPLOYMENT_TABLE_COLUMNS
} from '../utils/billing';

// TeamPartNumberTracking is the team-scoped counterpart to
// `CompanyPartNumberDeployments`. Both detail pages share the same chrome
// (overline + h4 + MonthPicker + KPI row + deployments table) so the user
// gets a consistent visual language across the company-wide and team-scoped
// billing views. The page fetches its own data from
// `/billing/teams/{teamId}/usage?month=YYYY-MM` and walks
// `billable_parts[]` to find the entry whose `part_number` matches the
// route param; that entry's `deployments[]` populates the table.
//
// The previous version read deployments from React Router state passed in
// by `TeamUsageReport.handlePartNumberClick`. Carrying state across routes
// meant the selected month had no place to live, so we refactored to a
// self-contained page that fetches by month — matching how the
// company-side page already works. The trade-off is one extra round-trip
// when navigating in from the team report, but it lets the MonthPicker be
// functional instead of decorative.

TeamPartNumberTracking.propTypes = {
  teamId: PropTypes.string,
  partNumber: PropTypes.string
};

TeamPartNumberTracking.defaultProps = {
  teamId: null,
  partNumber: null
};

export default function TeamPartNumberTracking({ teamId: teamIdProp, partNumber: partNumberProp }) {
  // Route params via useParams; props accepted for tests/storybook.
  const params = useParams();
  const teamId = teamIdProp || params.teamId;
  const partNumber = partNumberProp || params.partNumber;
  const { companyId } = params;

  // Seed the MonthPicker from the URL `?month=YYYY-MM` (same convention as
  // CompanyPartNumberDeployments). The team report appends the param when
  // a part row is clicked, so a deep link or browser refresh lands on the
  // requested month instead of snapping back to the current month.
  const [searchParams] = useSearchParams();
  const [selectedMonth, setSelectedMonth] = useState(() => initialMonthFromUrl(searchParams));
  // The chart's active-day filter lives on the page so a stale
  // selection doesn't survive a month change. `null` means "no filter
  // applied -- show all deployments of this month".
  const [activeDay, setActiveDay] = useState(null);

  const dispatch = useDispatch();
  // Wrap the snackbar dispatcher in useCallback so its identity is stable
  // across renders — the `refetch` callback below depends on it and would
  // otherwise be rebuilt on every render.
  const openSnackbar = useCallback((payload) => dispatch(setOpenSnackbar(payload)), [dispatch]);

  // `/billing/teams/{teamId}/usage` — same endpoint TeamUsageReport's
  // TeamDetail uses. The page now fetches it directly rather than relying
  // on the parent to pass data via router state.
  const { fetchData } = useVrackApi('get', `/billing/teams/${encodeURIComponent(teamId)}/usage`);

  const [report, setReport] = useState(null);
  // 404 from the team endpoint means the team has no usage for the
  // selected month — render the empty state instead of the generic error.
  const [notFound, setNotFound] = useState(false);
  // Local error state — surfaces a non-404 fetch failure to the page so
  // the error alert renders without depending on `useVrackApi`'s internal
  // `error` state (which is reset between fetches).
  const [fetchError, setFetchError] = useState(null);

  // YYYY-MM string for the `month` query param. formatMonthParam returns
  // null for null/invalid Dates so we fall back to omitting the param when
  // the picker hasn't been seeded yet.
  const apiMonth = formatMonthParam(selectedMonth);

  // Refetch on teamId or month change. Mirrors the same shape as the
  // CompanyBillingReport's hook: reset transient state at the top, branch
  // on 404 vs. genuine failure, only update state when the request
  // resolves.
  const refetch = useCallback(async () => {
    setNotFound(false);
    setFetchError(null);
    setReport(null);
    try {
      const params = apiMonth ? { month: apiMonth } : {};
      const response = await fetchData({ params });
      if (response) {
        setReport(response);
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
        return;
      }
      setFetchError(err);
      openSnackbar({
        message: err.response?.data?.message || 'Failed to load team billing report',
        severity: 'error'
      });
    }
  }, [apiMonth, fetchData, openSnackbar]);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, apiMonth]);

  // Drop the active-day filter when the user picks a different month.
  // A day from the previous month has no meaning on the new one.
  useEffect(() => {
    setActiveDay(null);
  }, [apiMonth]);

  // The team report returns the team's display name at the top level —
  // `team_name`. Fall back to the URL teamId when no report has loaded yet
  // so the breadcrumb and header still render during the loading window.
  const displayTeamName = report?.team_name || teamId;

  // Walk the report's `billable_parts[]` looking for the entry whose
  // `part_number` matches the route param. If we don't find one (either
  // because the report hasn't loaded, or because the API didn't include
  // this part for the selected month), treat the page as empty.
  const deployments = useMemo(() => {
    const part = Array.isArray(report?.billable_parts)
      ? report.billable_parts.find((p) => p.part_number === partNumber)
      : undefined;
    return Array.isArray(part?.deployments)
      ? part.deployments.map((d) => ({ ...toBaseDeploymentRow(d) }))
      : [];
  }, [report, partNumber]);

  // Compute the per-day peak instants once so the row highlight can
  // filter to racks alive at the bar's peak (not every rack that
  // overlapped the day window). The chart's bar height is the max
  // concurrent at some instant during the day; on busy days the
  // overlap set is wider than the peak-instant set, so we have to
  // resolve the peak instant here to keep the table in sync.
  const peakInstantsByDay = useMemo(() => {
    const result = computePeakConcurrentByDay(deployments, selectedMonth);
    if (!result) return {};
    const map = {};
    result.days.forEach((dayIso, idx) => {
      map[dayIso] = result.peakInstants[idx];
    });
    return map;
  }, [deployments, selectedMonth]);

  // Apply the active-day filter on top of the page-level deployments.
  // When the user has clicked a day, show every rack that overlapped
  // the day window -- the chart's bar is the peak instant, but the
  // table should still show every rack that was alive at some point
  // on that day so the user has full context. The highlight rule
  // (driven by `wasConcurrentOnDay` in `DeploymentsTableCard`)
  // distinguishes the contributing racks from the merely-overlapping
  // ones visually. Without `activeDay` the filter is a no-op and we
  // hand the full list to the table.
  const filteredDeployments = useMemo(() => {
    if (!activeDay) return deployments;
    return deployments.filter((d) => wasActiveOnDay(d.createdDate, d.deletedDate, activeDay));
  }, [deployments, activeDay]);

  const billingMonthLabel = selectedMonth ? format(selectedMonth, 'MMMM yyyy') : '';
  // `useVrackApi`'s `loading` flag is false on first mount until fetchData
  // is actually called. We want to show the spinner whenever we have no
  // report and we're not in the not-found / error branches — same pattern
  // as CompanyPartNumberDeployments.
  const showLoading = !report && !fetchError && !notFound;

  // Error branch: real API failure (network, 5xx, etc.) — show a generic
  // error alert. 404s flow through to the not-found banner below instead.
  if (fetchError && !notFound) {
    return (
      <Page title="Error">
        <Container maxWidth={false}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load team billing report.
          </Alert>
        </Container>
      </Page>
    );
  }

  if (showLoading) {
    return (
      <Page title="Loading...">
        <Container maxWidth={false}>
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        </Container>
      </Page>
    );
  }

  return (
    <Page title={`${partNumber} Deployment Tracking | Virtual Racks`}>
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          {/* Breadcrumb on the company-scoped URL renders
              Home → Billing → Company → Team → Part Number; on the legacy
              URL it renders Home → Team Usage → Team → Part Number. The
              `company`, `team`, and `part` route segments are delimiters
              and excluded from the trail so they aren't clickable. */}
          <Grid item xs={12}>
            <Breadcrumb
              labels={{
                [teamId]: displayTeamName,
                [partNumber]: partNumber
              }}
              exclude={companyId ? ['company', 'team', 'part'] : ['part']}
              sx={{ mb: 2 }}
            />
          </Grid>

          {/* Page header — overline + h4 mirrors the company-wide
              deployments page so the two detail pages share a single
              visual language. The MonthPicker on the right lets the user
              re-fetch this part's deployments for a different month. */}
          <Grid item xs={12}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              <Stack>
                <Typography variant="overline" color="primary">
                  Part Number Deployment
                </Typography>
                <Typography variant="h4">{partNumber}</Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <MonthPicker value={selectedMonth} onChange={setSelectedMonth} />
              </Stack>
            </Stack>
          </Grid>

          {/* 404 branch — the team has no usage recorded for the
              selected month. Keep the chrome rendered (so the user can
              pick a different month) and surface a small banner so the
              user knows the table is empty by design, not because of a
              bug. */}
          {notFound && (
            <Grid item xs={12}>
              <Alert severity="info" data-testid="usage-not-found-alert">
                No usage recorded for {displayTeamName} in {billingMonthLabel}.
              </Alert>
            </Grid>
          )}

          {/* KPI cards */}
          <Grid item xs={12}>
            <DeploymentsKpiRow
              partNumber={partNumber}
              deployments={deployments}
              month={selectedMonth}
            />
          </Grid>

          {/* Combined peak-usage + deployments card. Clicking a bar in
              the chart filters the table below to the racks active on
              that day; clicking the same bar again (or the "clear"
              link in the chart header) restores the full-month view.
              Both pieces are driven by the same `deployments` array
              the API returned so totals and table rows never drift. */}
          <Grid item xs={12}>
            <DeploymentsPeakUsageAndTableCard
              deployments={deployments}
              filteredDeployments={filteredDeployments}
              month={selectedMonth}
              activeDay={activeDay}
              peakInstant={activeDay ? peakInstantsByDay[activeDay] : null}
              onActiveDayChange={setActiveDay}
              partNumber={partNumber}
              description="Peak concurrent units in use, measured per day. Click a day to filter the deployments to racks active on that day."
              emptyChartMessage={`No deployments of ${partNumber} recorded for ${displayTeamName} in ${billingMonthLabel}.`}
              emptyTableMessage={`No deployments recorded for ${partNumber} in ${billingMonthLabel}.`}
              headLabel={DEPLOYMENT_TABLE_COLUMNS}
              defaultOrderBy="createdDate"
              defaultOrder="desc"
            />
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
